import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import i18n from "src/i18n/config";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch } from "src/store";
import { selectCurrentWorkspaceId, setCurrentWorkspaceId } from "src/store/app";

import { selectProducts } from "@dashboard/products/store/selectors/products";
import { Product } from "@dashboard/products/types/index";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { fetchProductsWithBuilds } from "@products/store";
import { getDefaultProduct } from "@products/utils/getDefaultProduct/getDefaultProduct";
import { showErrorToast } from "@shared/toasts/Toasts";

type ProductId = number | null;
type UseCurrentProduct = (products: Product[]) => [ProductId, (value: ProductId) => void];

export const useCurrentProduct: UseCurrentProduct = (products) => {
    const defaultProduct = getDefaultProduct(products);
    const [id, setId] = React.useState<ProductId>(defaultProduct?.id ?? null);

    const setCurrentProductId = (value: number | null) => {
        setId(value);
        UserSettingsService.setWorkspaceValue("productPage.lastSelectedProduct", value);
    };

    React.useEffect(() => {
        if (id !== defaultProduct?.id && id) {
            UserSettingsService.setWorkspaceValue("productPage.lastSelectedProduct", id);
        }
    }, [id, defaultProduct?.id]);

    return [id, setCurrentProductId];
};

function getProduct(products: Product[], codename: string | undefined, androidVersion: string | undefined) {
    return products.find((product) => product.codename === codename && product.androidVersion === androidVersion);
}

enum Error {
    PRODUCT_NOT_FOUND_ERROR = "PRODUCT_NOT_FOUND_ERROR",
    WORKSPACE_NOT_FOUND_ERROR = "WORKSPACE_NOT_FOUND_ERROR",
}
type Props = {
    setCurrentProductId: (id: number) => void;
};
export const useChangeProductFromUrl = ({ setCurrentProductId }: Props) => {
    const products = useSelector(selectProducts);
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { codename, androidVersion } = useParams();
    const hasProductParams = codename && androidVersion;

    const href = window.location.href;
    const hasWorkspaceParam = href.includes("workspaceId=");
    const workspaceParamId = hasWorkspaceParam ? Number(href.split("workspaceId=")[1]?.split("&")[0]) : undefined;

    const { data: workspace } = useCurrentWorkspace({
        workspaceId: workspaceParamId,
    });

    const checkProduct = async (): Promise<void> => {
        const product = getProduct(products, codename, androidVersion);
        if (product) {
            setCurrentProductId(product.id);
            return Promise.resolve();
        }

        return Promise.reject(Error.PRODUCT_NOT_FOUND_ERROR);
    };
    const checkWorkspace = async (): Promise<void> => {
        if (!workspaceParamId) {
            return Promise.reject();
        }

        if (currentWorkspaceId === workspaceParamId) {
            return Promise.reject();
        }

        if (workspace) {
            dispatch(setCurrentWorkspaceId(workspaceParamId));
            await dispatch(fetchProductsWithBuilds({ workspaceId: workspaceParamId }));
            return Promise.resolve();
        }
        return Promise.reject(Error.WORKSPACE_NOT_FOUND_ERROR);
    };

    return async () => {
        try {
            if (hasWorkspaceParam) {
                await checkWorkspace();
            }
            if (hasProductParams) {
                await checkProduct();
            }
        } catch (error) {
            if (error === Error.PRODUCT_NOT_FOUND_ERROR) {
                showErrorToast(i18n.t("pages:productPage.toast.donHaveAccessToProduct"));
                navigate("/dashboard/products", { replace: true });
            }
            if (error === Error.WORKSPACE_NOT_FOUND_ERROR) {
                showErrorToast(i18n.t("pages:productPage.toast.donHaveAccessToWorkspace"));
                navigate("/dashboard/products", { replace: true });
            }
        }
    };
};
